import {
  Dispatch, SetStateAction, useEffect, useRef, useState,
} from 'react';
import { MapContainer, ScaleControl } from 'react-leaflet';
import * as React from 'react';
import { Overlay } from '@x-guard/xgac-types/xgac';
import L, { Map } from 'leaflet';
import 'leaflet-toolbar';
import '@x-guard/xgac-leaflet-distortable-image';
import 'leaflet-toolbar/dist/leaflet.toolbar.css';
import { Identifier } from 'react-admin';
import { useLocaleState, useTranslate } from 'ra-core';
import {
  BooleanFilter,
  CenterControl,
  CustomLayerControl,
  FloorFilter,
} from '../../apps/bhvk/components/LeafletControls';
import { attachOverlayLayers } from '../../utils/overlayImage';

const AnyL = L as any;

const overlayMarkerOptions = {
  draggable: true,
  preventMarkerRemoval: false,
  snappable: false,
  allowRemoval: true,
};
export const overlayToolbarActions = [
  AnyL.DistortAction,
  AnyL.DragAction,
  AnyL.FreeRotateAction,
  AnyL.ScaleAction,
];
export const OverlayMap = (props: {
  overlays: Array<Overlay>;
  handleClickOnOverlay: (overlayId: string) => void;
  map: Map | null;
  setMap: Dispatch<SetStateAction<any>>;
  selectedOverlay: Identifier;
  isCreating: boolean;
  newMarkerPlaced: boolean;
  setNewMarkerPlaced: (newMarkerPlaced: boolean) => void;
  possibleFloors: number[];
  chosenFloors: number[];
  setChosenFloors: (floors: number[]) => void;
  filterInBounds: boolean;
  setFilterInBounds: Dispatch<SetStateAction<boolean>>;
}) => {

  const {
    overlays, map, setMap, isCreating, newMarkerPlaced, setNewMarkerPlaced,
  } = props;
  const [zoomed, setZoomed] = useState<boolean>(false);
  const [locale] = useLocaleState();
  const translate = useTranslate();
  const layersRef = useRef<ReturnType<typeof attachOverlayLayers> | null>(null);
  const overlaysRef = useRef(overlays);
  const selectedRef = useRef(props.selectedOverlay);
  const clickRef = useRef(props.handleClickOnOverlay);
  const flewForOverlay = useRef<Identifier | null>(null);
  const focusRef = useRef<() => void>(() => {});
  overlaysRef.current = overlays;
  selectedRef.current = props.selectedOverlay;
  clickRef.current = props.handleClickOnOverlay;
  focusRef.current = () => {

    if (!map) return;
    const selectedId = selectedRef.current;
    if (!selectedId) flewForOverlay.current = null;
    const shouldFly = flewForOverlay.current !== selectedId;
    if (shouldFly && selectedId) {

      const selected = overlaysRef.current.find((overlay) => overlay._id === selectedId);
      const points = selected?.points;
      if (points?.nw && points.ne && points.se && points.sw) {

        const layerBounds = L.latLngBounds(
          [points.nw.lat, points.nw.lng],
          [points.se.lat, points.se.lng],
        );
        layerBounds.extend([points.ne.lat, points.ne.lng]);
        layerBounds.extend([points.sw.lat, points.sw.lng]);
        if (!map.getBounds().contains(layerBounds)) {

          map.flyToBounds(layerBounds, { padding: [20, 20], duration: 1.5 });

        }

      }
      flewForOverlay.current = selectedId;

    }

    map.eachLayer((layer: any) => {

      if (!(layer instanceof AnyL.DistortableImageOverlay)) return;

      if (layer.options._id === selectedId) {

        const element = layer.getElement?.();
        if (element && !element.complete) {

          layer.once('load', () => focusRef.current());
          return;

        }
        layer.editing.enable();
        layer.setZIndex(999999);
        if (!layer.isSelected()) layer.select();
        return;

      }

      if (layer.editing?.enabled()) {

        layer.editing.disable();
        layer.deselect();

      }
      layer.setZIndex(layer.options._stackIndex ?? 0);
      const foundRecord = overlaysRef.current.find((overlay) => overlay._id === layer.options._id);
      if (foundRecord?.points) {

        layer.setCorners([
          L.latLng(foundRecord.points.nw.lat, foundRecord.points.nw.lng),
          L.latLng(foundRecord.points.ne.lat, foundRecord.points.ne.lng),
          L.latLng(foundRecord.points.sw.lat, foundRecord.points.sw.lng),
          L.latLng(foundRecord.points.se.lat, foundRecord.points.se.lng),
        ]);

      }

    });

  };

  const customTranslation = {
    tooltips: {
      placeMarker: translate('resources.overlays.text.place_marker'),
    },
  };
  map?.pm?.setLang(locale as any, customTranslation);

  const syncVisibleOverlays = () => {

    const selected = selectedRef.current;
    layersRef.current?.sync(overlaysRef.current, selected ? [String(selected)] : []);

  };

  useEffect(() => {

    if (!map) return undefined;

    const layers = attachOverlayLayers(map, {
      className: 'leaflet-overlay',
      editable: false,
      actions: overlayToolbarActions,
      mode: 'freeRotate',
      onCreate: (layer, overlay) => {

        const element = layer.getElement?.();
        if (element) {

          L.DomEvent.on(element, 'dblclick', (event) => {

            event.stopPropagation();
            clickRef.current(overlay._id);

          });

        }
        const notify = () => {

          if (overlay._id === selectedRef.current) {

            focusRef.current();
            return;

          }
          const foundRecord = overlaysRef.current.find((item) => item._id === overlay._id);
          if (!foundRecord?.points) return;
          layer.setCorners([
            L.latLng(foundRecord.points.nw.lat, foundRecord.points.nw.lng),
            L.latLng(foundRecord.points.ne.lat, foundRecord.points.ne.lng),
            L.latLng(foundRecord.points.sw.lat, foundRecord.points.sw.lng),
            L.latLng(foundRecord.points.se.lat, foundRecord.points.se.lng),
          ]);

        };
        if (element?.complete && element.naturalWidth) notify();
        else layer.once?.('load', notify);

      },
    });
    layersRef.current = layers;
    syncVisibleOverlays();

    return () => {

      layers.destroy();
      layersRef.current = null;

    };

  }, [map]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {

    syncVisibleOverlays();
    focusRef.current();

  }, [overlays, props.selectedOverlay, map]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {

    if (!map) return undefined;

    const onMoveEnd = () => syncVisibleOverlays();
    map.on('moveend', onMoveEnd);
    return () => {

      map.off('moveend', onMoveEnd);

    };

  }, [map]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {

    if (!map || overlays.length === 0 || zoomed) return;

    const allBounds = overlays.map((innerOverlay) => (

      L.polygon([innerOverlay.points.ne, innerOverlay.points.nw, innerOverlay.points.se, innerOverlay.points.sw])

    ));
    map.fitBounds(allBounds.map((bounds) => bounds?.getBounds()) as any);
    setZoomed(true);

  }, [overlays, map, zoomed]);

  useEffect(() => {

    if (!map) {

      return;

    }
    if (isCreating) {

      if (!newMarkerPlaced) {

        map?.pm?.enableDraw('Marker', {
          snappable: false,
          markerEditable: true,
          finishOn: 'click',
          markerStyle: {
            draggable: true,
            icon: L.divIcon({
              className: 'marker',
              iconAnchor: [11, 36],
              popupAnchor: [0, -24],
              html: `<div class="pin pincolor-1972b9"></div><style>
  .pin.pincolor-1972b9 {
    border-color: #1972b9;
  }
  .pin.pincolor-1972b9::after { border-top-color: #1972b9 }
</style>`,
            }),
          },
        });

        map?.on('pm:create', (e: any) => {

          map?.pm.disableDraw();
          setNewMarkerPlaced(true);
          e.layer.myId = -1;
          e.layer.pm?.enable(overlayMarkerOptions);

        });

      }

    } else {

      map?.pm?.disableDraw();
      map?.eachLayer((layer: any) => {

        if (layer instanceof AnyL.Marker) {

          map?.removeLayer(layer);

        }

      });

    }

  }, [isCreating, newMarkerPlaced, map]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <MapContainer
      ref={(ref: any) => setMap(ref)}
      center={[52.1009166, 5.6462914]}
      zoom={17}
      minZoom={4}
      renderer={L.canvas()}
      zoomControl={false}
      style={{ height: '100%' }}>
      <ScaleControl position="bottomright" imperial={false}/>
      <div>
        <div className="leaflet-control-container-custom">
          <CustomLayerControl
            map={map}
            toggleVisible={true}
          />
          <CenterControl onClick={() => {

            setZoomed(false);

          }}/>
        </div>
        <div className="second-filter-div">
          <FloorFilter floors={props.possibleFloors} selectedFloors={props.chosenFloors} onChange={props.setChosenFloors}/>
          <BooleanFilter value={props.filterInBounds} onClick={() => {

            props.setFilterInBounds(!props.filterInBounds);

          }} label="resources.zones.text.filter_in_bounds"/>
        </div>
      </div>
    </MapContainer>
  );

};
